/**
 * Email-hub "mark as bill" hook.
 *
 * NOTE: this lives under `_stubs/` only for import-path stability (3 callers
 * import it from here). It is a REAL implementation, NOT a stub.
 *
 * WHY it can't just use `@quotech/bills`: that package depends on
 * `@quotech/email-hub` (real bills UI is built on top of the email hub), so
 * email-hub importing it would be a circular workspace dependency. Instead we
 * call the SAME backend endpoint the real `@quotech/bills.useMarkEmailAsBill`
 * uses — `POST /api/emails/<id>/mark-as-bill/` (force-classify the email as a
 * bill + get_or_create the ExtractedBill). The resulting bill is viewable in
 * SAM / CFO; the email hub just needs the action to PERSIST.
 *
 * BUG-2026-06-14-EMAIL-HUB-BILLS-NOOP: previously a no-op that returned null
 * with no HTTP, so "Send to Bills" / "Allocate to Bills" / attachment
 * "Mark as bill" silently did nothing.
 *
 * Contract: `{ run, busy, error }` — callers (SmartActionBar, EmailContextMenu,
 * AttachmentList) consume the SAM-mirrored `MarkAsBillResponse` shape unchanged.
 */
import { useCallback, useState } from 'react'

import {
  buildMarkAsBillBody,
  emailApi,
  markEmailAsBillPath,
} from '@quotech/tenant-shared/email/api'

export interface MarkAsBillResponse {
  ok: boolean
  /** True when this call CREATED a new bill row, false if the email was
   * already linked to an existing bill (idempotent re-trigger). Mirrors
   * SAM's real billsApi.markEmailAsBill response shape so callers compile
   * unchanged. */
  created?: boolean
  bill_id?: string | number
  message?: string
}

/** Backend `POST /api/emails/<id>/mark-as-bill/` response shape. */
interface MarkAsBillApiResponse {
  bill_id: string | number
  status: string
  created: boolean
  email_id: string
  attachment_id?: string | number | null
}

/**
 * Pure I→O: POST the mark-as-bill request and map the backend response to
 * `MarkAsBillResponse`. Exported for direct unit testing — the hook below is
 * a thin busy/error wrapper around this.
 *
 * Throws on a failed request (the hook catches and surfaces it as `error`).
 */
export async function markEmailAsBill(
  emailId: string,
  attachmentId?: string | number | null,
): Promise<MarkAsBillResponse> {
  const resp = await emailApi<MarkAsBillApiResponse>(
    markEmailAsBillPath(emailId),
    {
      method: 'POST',
      body: JSON.stringify(buildMarkAsBillBody(attachmentId)),
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return {
    ok: true,
    created: resp.created,
    bill_id: resp.bill_id,
    message: resp.created ? 'Bill created' : 'Linked to existing bill',
  }
}

export function useMarkEmailAsBill() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async (
      emailId: string,
      attachmentId?: string | number | null,
    ): Promise<MarkAsBillResponse | null> => {
      setBusy(true)
      setError(null)
      try {
        return await markEmailAsBill(emailId, attachmentId)
      } catch (e) {
        setError((e as Error).message || 'Could not mark as bill')
        return null
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  return { run, busy, error }
}
